import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import { Sparkles, Flame, Utensils, ArrowRight, Plus, LogIn, X, Lock, Crown, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useBilling } from "../context/BillingContext";
import { openStripeLink } from "../lib/stripe";
import { toast } from "sonner";
import DateNightFeature from "../components/DateNightFeature";
import VibeEmoji from "../components/VibeEmoji";
import FloatingHearts from "../components/FloatingHearts";
import Header from "../components/Header";
import Footer from "../components/Footer";
import BottomTabBar from "../components/BottomTabBar";
import MarketingLanding from "../components/MarketingLanding"; // eslint-disable-line no-unused-vars -- preserved for optional re-enable behind a feature flag
import HomeView from "../components/HomeView";
import PlayRemotelyEntry from "../components/PlayRemotelyEntry";
import PlayRemotelyCreateRoom from "../components/PlayRemotelyCreateRoom";
import PlayRemotelyPickPack from "../components/PlayRemotelyPickPack";
import PlayRemotelyInvite from "../components/PlayRemotelyInvite";
import PlayRemotelyJoinRoom from "../components/PlayRemotelyJoinRoom";
import PlayTogetherEntry from "../components/PlayTogetherEntry";
import PlayTogetherGame from "../components/PlayTogetherGame";
import PlayGroupEntry from "../components/PlayGroupEntry";
import PlayGroupCreate from "../components/PlayGroupCreate";
import PlayGroupSquad from "../components/PlayGroupSquad";
import PlayGroupPickPack from "../components/PlayGroupPickPack";
import PlayGroupJoin from "../components/PlayGroupJoin";
import PlayGroupGameplay from "../components/PlayGroupGameplay";
import PlayGroupConfessions from "../components/PlayGroupConfessions";
import Onboarding from "./Onboarding"; // eslint-disable-line no-unused-vars -- preserved for optional re-enable behind a feature flag
import Welcome from "./Welcome"; // eslint-disable-line no-unused-vars -- preserved for optional re-enable behind a feature flag

export default function Landing() {
  const { user } = useAuth();
  const { code: urlCode } = useParams();
  // Per product decision: send every visitor straight to the game home.
  // No marketing landing, no welcome carousel. The Welcome + MarketingLanding
  // components stay in the codebase in case we ever want to re-enable them
  // behind a feature flag.
  return <Dashboard user={user} urlCode={urlCode} />;
}

function Dashboard({ user, urlCode }) {
  const nav = useNavigate();
  const { isPackUnlocked, entitlements, openPackPreview, catalog } = useBilling();
  const [mode, setMode] = useState(urlCode ? "join" : null);
  const [showRemotelyEntry, setShowRemotelyEntry] = useState(false);
  const [showRemotelyCreateRoom, setShowRemotelyCreateRoom] = useState(false);
  const [showRemotelyPickPack, setShowRemotelyPickPack] = useState(false);
  const [createDraft, setCreateDraft] = useState(null); // { roomName, hostName, avatarId }
  const [inviteRoom, setInviteRoom] = useState(null);
  const [showTogetherEntry, setShowTogetherEntry] = useState(false);
  const [showGroupEntry, setShowGroupEntry] = useState(false);
  const [showGroupCreate, setShowGroupCreate] = useState(false);
  const [groupSquadCode, setGroupSquadCode] = useState(null);   // code → render Squad lobby
  const [groupSquadIsHost, setGroupSquadIsHost] = useState(false);
  const [showGroupPickPack, setShowGroupPickPack] = useState(false);
  const [groupJoinCode, setGroupJoinCode] = useState(null);  // /group-join/:code deep link
  const [groupGameplay, setGroupGameplay] = useState(null);  // {code, playerId, isHost}
  const [togetherActive, setTogetherActive] = useState(false);
  const [name, setName] = useState(user?.name || "");
  const [code, setCode] = useState(urlCode ? urlCode.toUpperCase() : "");
  const [loading, setLoading] = useState(false);
  const [packs, setPacks] = useState([]);
  const [selectedPackId, setSelectedPackId] = useState(null);

  // Auto-open the matching entry when arriving via a share/deep link
  useEffect(() => {
    const p = window.location.pathname;
    if (p === "/together") setShowTogetherEntry(true);
    else if (p === "/remotely") setShowRemotelyEntry(true);
    else if (p === "/group") setShowGroupEntry(true);
    else if (p.startsWith("/group-join/")) {
      const c = p.split("/group-join/")[1] || "";
      if (c) setGroupJoinCode(c.toUpperCase());
    }
  }, []);

  useEffect(() => {
    if (urlCode) {
      setMode("join");
      setCode(urlCode.toUpperCase());
    }
    api.listPacks().then(setPacks).catch(() => {});
  }, [urlCode]);

  const openAction = (action) => {
    // Persist pack pre-selection so the lobby auto-applies it.
    if (selectedPackId) {
      localStorage.setItem("preselected_source", "pack");
      localStorage.setItem("preselected_pack_id", selectedPackId);
    } else {
      localStorage.removeItem("preselected_source");
      localStorage.removeItem("preselected_pack_id");
    }
    setSelectedPackId(null);
    setMode(action);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handlePrimaryCreate = () => openAction("create");

  const handleCreate = async () => {
    if (!name.trim()) return toast.error("Please enter your name");
    setLoading(true);
    try {
      const room = await api.createRoom({
        host_name: name.trim(),
        user_id: user?.id || null,
      });
      localStorage.setItem("player_role", "host");
      localStorage.setItem("player_name", name.trim());
      nav(`/room/${room.code}`);
    } catch (e) {
      toast.error("Couldn't create room");
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async () => {
    if (!name.trim()) return toast.error("Please enter your name");
    if (!code.trim() || code.trim().length < 4) return toast.error("Enter a valid room code");
    setLoading(true);
    try {
      const guestAvatar = localStorage.getItem("remote_avatar_id") || "purple-w";
      const room = await api.joinRoom({
        code: code.trim().toUpperCase(),
        guest_name: name.trim(),
        user_id: user?.id || null,
        avatar_id: guestAvatar,
      });
      localStorage.setItem("player_role", "guest");
      localStorage.setItem("player_name", name.trim());
      localStorage.setItem("remote_avatar_id", guestAvatar);
      nav(`/room/${room.code}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't join room");
    } finally {
      setLoading(false);
    }
  };

  // Hide global Header + BottomTabBar on all immersive cinematic pages
  const isImmersive =
    mode === "join" ||
    showRemotelyEntry ||
    showRemotelyCreateRoom ||
    showRemotelyPickPack ||
    !!inviteRoom ||
    showTogetherEntry ||
    showGroupEntry ||
    showGroupCreate ||
    !!groupSquadCode ||
    showGroupPickPack ||
    !!groupJoinCode ||
    !!groupGameplay ||
    togetherActive;

  return (
    <div className="vc-bg relative overflow-hidden min-h-screen">
      {!isImmersive && <Header />}

      {!isImmersive && (
        <HomeView
          user={user}
          onRemotelyTap={() => {
            setShowRemotelyEntry(true);
            window.scrollTo({ top: 0, behavior: "instant" });
          }}
          onTogetherTap={() => {
            setShowTogetherEntry(true);
            window.scrollTo({ top: 0, behavior: "instant" });
          }}
          onGroupTap={() => {
            setShowGroupEntry(true);
            window.scrollTo({ top: 0, behavior: "instant" });
          }}
          onSelectPack={(p) => {
            if (!isPackUnlocked(p.id)) {
              openPackPreview(p);
              return;
            }
            setSelectedPackId(p.id);
            localStorage.setItem("preselected_source", "pack");
            localStorage.setItem("preselected_pack_id", p.id);
            setMode("create");
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        />
      )}

      {showRemotelyEntry && !mode && !showRemotelyCreateRoom && !showRemotelyPickPack && !inviteRoom && !togetherActive && (
        <PlayRemotelyEntry
          onBack={() => setShowRemotelyEntry(false)}
          onCreateRoom={(intent) => {
            if (intent) localStorage.setItem("remote_share_intent", intent);
            setShowRemotelyCreateRoom(true);
            window.scrollTo({ top: 0, behavior: "instant" });
          }}
          onJoinRoom={() => {
            setShowRemotelyEntry(false);
            setMode("join");
          }}
        />
      )}

      {showRemotelyCreateRoom && !showRemotelyPickPack && !inviteRoom && !mode && !togetherActive && (
        <PlayRemotelyCreateRoom
          defaultName={user?.name || localStorage.getItem("player_name") || ""}
          onBack={() => setShowRemotelyCreateRoom(false)}
          onContinue={(draft) => {
            setCreateDraft(draft);
            setShowRemotelyCreateRoom(false);
            setShowRemotelyPickPack(true);
            window.scrollTo({ top: 0, behavior: "instant" });
          }}
        />
      )}

      {showRemotelyPickPack && !inviteRoom && !mode && !togetherActive && (
        <PlayRemotelyPickPack
          loading={loading}
          onBack={() => {
            setShowRemotelyPickPack(false);
            setShowRemotelyCreateRoom(true);
          }}
          onContinue={async ({ packId }) => {
            if (!createDraft) return;
            if (!isPackUnlocked(packId)) {
              const p = packs.find((x) => x.id === packId);
              if (p) {
                openPackPreview(p);
                return;
              }
            }
            setLoading(true);
            try {
              const room = await api.createRoom({
                host_name: createDraft.hostName,
                user_id: user?.id || null,
                avatar_id: createDraft.avatarId,
                room_name: createDraft.roomName,
              });
              localStorage.setItem("player_role", "host");
              localStorage.setItem("player_name", createDraft.hostName);
              localStorage.setItem("remote_avatar_id", createDraft.avatarId);
              localStorage.setItem("remote_preselected_pack", packId);
              setShowRemotelyPickPack(false);
              setInviteRoom(room);
            } catch (e) {
              toast.error("Couldn't create room");
            } finally {
              setLoading(false);
            }
          }}
        />
      )}

      {inviteRoom && !mode && !togetherActive && (
        <PlayRemotelyInvite
          room={inviteRoom}
          onBack={() => {
            setInviteRoom(null);
            setShowRemotelyPickPack(true);
          }}
          onJoinInstead={() => {
            setInviteRoom(null);
            setShowRemotelyEntry(false);
            setMode("join");
          }}
          onContinue={() => {
            const code = inviteRoom.code;
            setInviteRoom(null);
            nav(`/room/${code}`);
          }}
        />
      )}

      {showTogetherEntry && !togetherActive && (
        <PlayTogetherEntry
          onBack={() => setShowTogetherEntry(false)}
          onStart={() => {
            setShowTogetherEntry(false);
            setTogetherActive(true);
          }}
        />
      )}

      {groupJoinCode && (
        <PlayGroupJoin
          code={groupJoinCode}
          onBack={() => {
            setGroupJoinCode(null);
            nav("/");
          }}
          onJoined={(code) => {
            setGroupJoinCode(null);
            setGroupSquadCode(code);
            setGroupSquadIsHost(false);
          }}
        />
      )}

      {showGroupEntry && (
        <PlayGroupEntry
          onBack={() => setShowGroupEntry(false)}
          onPickVibe={(id) => { void id; }}
          onStart={() => {
            setShowGroupEntry(false);
            setShowGroupCreate(true);
          }}
          onJoin={() => {
            // Group "Join with code" uses the same join UI as Remotely for now
            setShowGroupEntry(false);
            setMode("join");
          }}
        />
      )}

      {showGroupCreate && (
        <PlayGroupCreate
          onBack={() => {
            setShowGroupCreate(false);
            setShowGroupEntry(true);
          }}
          onReady={(room) => {
            setShowGroupCreate(false);
            setGroupSquadCode(room.code);
            setGroupSquadIsHost(true);
          }}
        />
      )}

      {groupSquadCode && !showGroupPickPack && !groupGameplay && (
        <PlayGroupSquad
          code={groupSquadCode}
          isHost={groupSquadIsHost}
          onBack={() => {
            setGroupSquadCode(null);
            setShowGroupEntry(true);
          }}
          onPickPack={() => setShowGroupPickPack(true)}
          onGameStarted={(room) => {
            // Triggered for guests when host starts the round (polled)
            const me = (room.players || []).find((p) => p.name === (localStorage.getItem("player_name") || ""));
            setGroupGameplay({ code: room.code, playerId: me?.id || "", isHost: false });
            setGroupSquadCode(null);
          }}
        />
      )}

      {showGroupPickPack && groupSquadCode && (
        <PlayGroupPickPack
          code={groupSquadCode}
          onBack={() => setShowGroupPickPack(false)}
          onLaunched={(room) => {
            const me = (room.players || []).find((p) => p.id === room.host_id);
            setGroupGameplay({
              code: room.code,
              playerId: me?.id || room.host_id,
              isHost: true,
            });
            setShowGroupPickPack(false);
            setGroupSquadCode(null);
          }}
        />
      )}

      {groupGameplay && (
        <GroupGameplayDispatcher
          code={groupGameplay.code}
          currentPlayerId={groupGameplay.playerId}
          isHost={groupGameplay.isHost}
          onLeave={() => {
            setGroupGameplay(null);
            nav("/");
          }}
        />
      )}

      {togetherActive && (
        <PlayTogetherGame
          packId={null}
          onExit={() => setTogetherActive(false)}
        />
      )}

      {mode === "join" && (
        <PlayRemotelyJoinRoom
          code={code}
          defaultName={user?.name || localStorage.getItem("player_name") || ""}
          loading={loading}
          onBack={() => {
            setMode(null);
            // If they arrived via /join/:code deep link, also drop them back to the home
            if (urlCode) nav("/");
          }}
          onJoin={async ({ name: joinName, avatarId }) => {
            setName(joinName);
            setLoading(true);
            try {
              const room = await api.joinRoom({
                code: (code || "").trim().toUpperCase(),
                guest_name: joinName,
                user_id: user?.id || null,
                avatar_id: avatarId,
              });
              localStorage.setItem("player_role", "guest");
              localStorage.setItem("player_name", joinName);
              localStorage.setItem("remote_avatar_id", avatarId);
              nav(`/room/${room.code}`);
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Couldn't join room");
            } finally {
              setLoading(false);
            }
          }}
        />
      )}

      {mode === "create" && (
        <main className="relative z-10 max-w-5xl mx-auto px-6 md:px-10 pt-10 md:pt-12 pb-20">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-glow rounded-3xl p-7 md:p-8 max-w-md mb-12"
          >
            <h3 className="font-display text-2xl font-bold text-white mb-5">
              Start a room
            </h3>
            <label className="block text-xs font-semibold text-[#C9C7D9] mb-2 uppercase tracking-wider">
              Your name
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jamie"
              className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-3.5 text-white placeholder:text-[#C9C7D9]/60 focus:outline-none focus:ring-2 focus:ring-[#FF4D8D]/60 focus:border-transparent mb-5"
              data-testid="player-name-input"
            />
            <div className="flex gap-3">
              <button
                onClick={handleCreate}
                disabled={loading}
                className="flex-1 btn-gradient rounded-full px-6 py-3.5 font-bold disabled:opacity-60 active:scale-95 transition"
                data-testid="confirm-action-button"
              >
                {loading ? "..." : "Create room"}
              </button>
              <button
                onClick={() => setMode(null)}
                className="px-5 py-3.5 rounded-full font-semibold text-[#C9C7D9] hover:text-white hover:bg-white/5 transition"
                data-testid="cancel-action-button"
              >
                Back
              </button>
            </div>
          </motion.div>
        </main>
      )}

      {/* Legacy "mode-set" extras (kept for now while we ship Phase 1 home UI) */}
      {mode === "create" && (
        <main className="relative z-10 max-w-5xl mx-auto px-6 md:px-10 pb-20">
        {/* Premium offers strip — Spicy 24hr deal + Unlock-all bundle */}
        {!entitlements.is_premium && !entitlements.has_bundle && (
          <PremiumOffersStrip catalog={catalog} user={user} />
        )}

        {/* Limited-time featured experience: Date Night */}
        {(() => {
          const dateNight = packs.find((p) => p.id === "date-night");
          if (!dateNight) return null;
          return (
            <section className="mt-14 md:mt-16">
              <DateNightFeature
                pack={dateNight}
                onClick={() => {
                  setSelectedPackId(dateNight.id);
                  localStorage.setItem("preselected_source", "pack");
                  localStorage.setItem("preselected_pack_id", dateNight.id);
                  setMode("create");
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              />
            </section>
          );
        })()}

        {/* Packs — featured top 3 with Spicy dominant */}
        <section className="mt-14 md:mt-16">
          <div className="flex items-end justify-between mb-4 gap-4 flex-wrap">
            <div>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-white mb-1">
                Tonight's <span className="gradient-text">vibe</span>
              </h2>
              <p className="text-[#C9C7D9] text-sm">
                Start safe · try spicy · go deep.
              </p>
            </div>
            <button
              onClick={() => nav("/pricing")}
              className="text-xs font-bold text-[#FF4D8D] hover:text-white inline-flex items-center gap-1.5 transition"
              data-testid="view-all-packs-link"
            >
              See all plans <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
          <FeaturedPacks
            packs={packs}
            isPackUnlocked={isPackUnlocked}
            onOpenPreview={(p) => openPackPreview(p)}
            onSelectFree={(p) => {
              setSelectedPackId(p.id);
              localStorage.setItem("preselected_source", "pack");
              localStorage.setItem("preselected_pack_id", p.id);
              setMode("create");
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          />
        </section>

        <p className="text-center text-xs text-[#C9C7D9] mt-14 uppercase tracking-[0.3em] font-semibold">
          Free · No download · 2 players
        </p>
        <p
          className="text-center text-[10px] text-[#9B98AE] mt-3 uppercase tracking-[0.3em] font-bold"
          data-testid="landing-age-disclaimer"
        >
          18+ · Some packs include adult content
        </p>
        {/* Bottom-tab safe space so the last content isn't covered by the tab bar */}
        <div className="h-44" aria-hidden />
        </main>
      )}
      <Footer />
      {!isImmersive && <BottomTabBar active="play" />}
    </div>
  );
}

function FeaturedPacks({ packs, isPackUnlocked, onOpenPreview, onSelectFree }) {
  const [browseOpen, setBrowseOpen] = useState(false);
  const map = Object.fromEntries(packs.map((p) => [p.id, p]));
  const classicTrigger = map["first-date"]; // "Truth or Dare 🔥" (free, safe entry)
  const spicy = map["spicy-mode"];
  const deep = map["deep-talk"];

  if (!classicTrigger || !spicy || !deep) {
    return null;
  }

  // Exclude the 3 hero packs AND the Date Night experience (it has its own
  // dedicated featured banner above this section).
  const extras = packs.filter(
    (p) => !["first-date", "spicy-mode", "deep-talk", "date-night"].includes(p.id)
  );

  return (
    <>
      <div className="grid md:grid-cols-3 gap-3 items-stretch">
        <FeaturedClassicCard
          pack={classicTrigger}
          onClick={() => onSelectFree(classicTrigger)}
        />
        <FeaturedHero
          pack={spicy}
          unlocked={isPackUnlocked(spicy.id)}
          onClick={() => onSelectFree(spicy)}
          onOpenPreview={onOpenPreview}
        />
        <FeaturedDeepCard
          pack={deep}
          unlocked={isPackUnlocked(deep.id)}
          onClick={() => (isPackUnlocked(deep.id) ? onSelectFree(deep) : onOpenPreview(deep))}
        />
      </div>

      <div className="mt-5">
        <button
          onClick={() => setBrowseOpen((v) => !v)}
          className="w-full text-sm font-bold text-[#C9C7D9] hover:text-white inline-flex items-center justify-center gap-2 py-3 border border-white/10 rounded-full bg-white/[0.02] hover:bg-white/5 transition"
          data-testid="browse-more-packs-toggle"
          aria-expanded={browseOpen}
        >
          Browse more packs 👀
          <ArrowRight
            className={`w-3.5 h-3.5 transition-transform ${browseOpen ? "rotate-90" : ""}`}
          />
        </button>
        <AnimatePresence initial={false}>
          {browseOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.28, ease: "easeOut" }}
              className="overflow-hidden"
              data-testid="browse-more-packs-panel"
            >
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4">
                {extras.map((p) => (
                  <PackShowcaseCard
                    key={p.id}
                    pack={p}
                    unlocked={isPackUnlocked(p.id)}
                    onOpen={() =>
                      isPackUnlocked(p.id) ? onSelectFree(p) : onOpenPreview(p)
                    }
                  />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
}

function FeaturedClassicCard({ pack, onClick }) {
  return (
    <motion.button
      whileHover={{ y: -3 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      data-testid="featured-classic"
      className="relative text-left rounded-3xl p-5 overflow-hidden border"
      style={{
        background: "rgba(26,26,36,0.7)",
        borderColor: "rgba(255,255,255,0.08)",
      }}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-4xl">🔥</span>
        <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#00E5FF]">Free · Start here</span>
      </div>
      <p className="font-bold text-white text-xl leading-tight mb-1 tracking-tight">
        Truth or Dare
      </p>
      <p className="text-xs text-[#C9C7D9] leading-snug mb-4">
        Classic get-to-know-you energy. Safe entry to the whole game.
      </p>
      <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.25em] text-white/80">
        Tap to play <ArrowRight className="w-3 h-3" />
      </span>
    </motion.button>
  );
}

function FeaturedHero({ pack, unlocked, onClick, onOpenPreview }) {
  const tag = pack.tag || "🔥 Most Popular";
  return (
    <motion.button
      whileHover={{ y: -4 }}
      whileTap={{ scale: 0.98 }}
      animate={{ scale: [1, 1.015, 1] }}
      transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
      onClick={() => (unlocked ? onClick() : onOpenPreview(pack))}
      data-testid="featured-hero-spicy"
      className="relative text-left rounded-3xl p-6 overflow-hidden border-2 md:scale-[1.06] md:-my-1"
      style={{
        background:
          "linear-gradient(135deg, rgba(255,77,141,0.30), rgba(123,97,255,0.22)), radial-gradient(circle at 30% 20%, rgba(255,77,141,0.45), rgba(15,15,20,0.95) 70%)",
        borderColor: "rgba(255,77,141,0.9)",
        boxShadow:
          "0 24px 70px rgba(0,0,0,0.45), 0 0 80px rgba(255,77,141,0.5), 0 0 0 1px rgba(255,77,141,0.35)",
      }}
    >
      {/* Shimmer sweep */}
      <motion.span
        aria-hidden
        className="absolute inset-0 pointer-events-none rounded-3xl"
        animate={{ backgroundPositionX: ["0%", "200%"] }}
        transition={{ duration: 3.2, repeat: Infinity, ease: "linear" }}
        style={{
          background: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.22) 50%, transparent 100%)",
          backgroundSize: "45% 100%",
          backgroundRepeat: "no-repeat",
        }}
      />
      {/* Inner pulsing glow */}
      <motion.span
        aria-hidden
        className="absolute inset-0 pointer-events-none rounded-3xl"
        animate={{ opacity: [0.55, 0.85, 0.55] }}
        transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
        style={{ boxShadow: "inset 0 0 60px rgba(255,77,141,0.45)" }}
      />
      <div className="relative">
        <div className="flex items-center justify-between mb-3">
          <span className="inline-flex items-center text-[10px] font-bold uppercase tracking-[0.25em] bg-[#FF4D8D] text-white px-2.5 py-0.5 rounded-full">
            {tag}
          </span>
          {unlocked ? (
            <span className="inline-flex items-center text-[10px] font-bold uppercase tracking-[0.2em] text-[#00E5FF] bg-[#00E5FF]/10 border border-[#00E5FF]/40 px-2 py-0.5 rounded-full">
              Owned
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.2em] text-[#FF4D8D] bg-black/30 border border-[#FF4D8D]/50 px-2 py-0.5 rounded-full">
              <Lock className="w-2.5 h-2.5" /> $0.99
            </span>
          )}
        </div>
        <motion.span
          className="text-5xl block mb-3"
          animate={{ rotate: [0, 10, -8, 0], scale: [1, 1.1, 1] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
          style={{ filter: "drop-shadow(0 0 16px rgba(255,77,141,0.8))" }}
        >
          {pack.emoji}
        </motion.span>
        <p className="font-display font-bold text-white text-2xl md:text-[26px] leading-tight mb-1">
          {pack.name}
        </p>
        <p className="text-sm text-white/80 leading-snug italic mb-4">
          "{pack.description}"
        </p>
        <span
          className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.25em] text-white bg-gradient-to-r from-[#FF4D8D] to-[#7B61FF] px-3.5 py-1.5 rounded-full"
        >
          <Flame className="w-3.5 h-3.5" /> {unlocked ? "Tap to play" : "Peek inside"}
        </span>
      </div>
    </motion.button>
  );
}

function FeaturedDeepCard({ pack, unlocked, onClick }) {
  return (
    <motion.button
      whileHover={{ y: -3 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      data-testid="featured-deep"
      className="relative text-left rounded-3xl p-5 overflow-hidden border"
      style={{
        background: "linear-gradient(135deg, rgba(123,97,255,0.16), rgba(0,229,255,0.10)), rgba(15,15,20,0.7)",
        borderColor: "rgba(123,97,255,0.5)",
        boxShadow: "0 0 30px rgba(123,97,255,0.25)",
      }}
    >
      <motion.span
        aria-hidden
        className="absolute inset-0 pointer-events-none rounded-3xl"
        animate={{ opacity: [0.3, 0.55, 0.3] }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        style={{ boxShadow: "inset 0 0 32px rgba(123,97,255,0.3)" }}
      />
      <div className="relative">
        <div className="flex items-center justify-between mb-3">
          <span className="text-4xl">💭</span>
          {unlocked ? (
            <span className="inline-flex items-center text-[10px] font-bold uppercase tracking-[0.2em] text-[#00E5FF] bg-[#00E5FF]/10 border border-[#00E5FF]/40 px-2 py-0.5 rounded-full">
              Owned
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.2em] text-[#7B61FF] bg-[#7B61FF]/10 border border-[#7B61FF]/40 px-2 py-0.5 rounded-full">
              <Lock className="w-2.5 h-2.5" /> Paid
            </span>
          )}
        </div>
        <p className="font-bold text-white text-xl leading-tight mb-1 tracking-tight">
          {pack.name}
        </p>
        <p className="text-xs text-[#C9C7D9] leading-snug mb-4">
          {pack.description}
        </p>
        <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.25em] text-white/80">
          {unlocked ? "Tap to play" : "Peek inside 👀"} <ArrowRight className="w-3 h-3" />
        </span>
      </div>
    </motion.button>
  );
}

function PackShowcaseCard({ pack, unlocked, onOpen }) {
  // Each pack now has its own visual mood (gradient + emoji animation).
  // Paid (locked) packs get the pack's signature gradient bg + glow; free
  // packs stay on the neutral surface with just the per-pack emoji vibe.
  const isPremium = !unlocked;
  const vibe = pack.vibe || {};
  const accent = vibe.accent || pack.color || "#FF4D8D";

  return (
    <motion.button
      type="button"
      onClick={onOpen}
      whileHover={{ y: -3 }}
      whileTap={{ scale: 0.98 }}
      data-testid={`pack-showcase-${pack.id}`}
      className="relative text-left rounded-2xl p-4 border overflow-hidden transition"
      style={
        isPremium
          ? {
              background: vibe.gradient
                ? `${vibe.gradient}, rgba(15,15,20,0.4)`
                : "linear-gradient(135deg, rgba(255,77,141,0.18), rgba(123,97,255,0.18)), rgba(15,15,20,0.7)",
              borderColor: `${accent}AA`,
              boxShadow: `0 0 40px ${accent}66, inset 0 0 20px rgba(255,255,255,0.08)`,
            }
          : {
              background: "rgba(26,26,36,0.6)",
              borderColor: "rgba(255,255,255,0.08)",
            }
      }
    >
      {isPremium && (
        <motion.span
          aria-hidden
          className="absolute inset-0 pointer-events-none rounded-2xl"
          animate={{ opacity: [0.35, 0.6, 0.35] }}
          transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
          style={{ boxShadow: `inset 0 0 32px ${accent}40` }}
        />
      )}
      <div className="relative">
        <div className="flex items-center justify-between mb-2">
          <VibeEmoji
            emoji={pack.emoji}
            animationKey={vibe.animation}
            size="text-3xl"
            glow={isPremium ? accent : null}
          />
          {isPremium ? (
            <span
              className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.2em] px-2 py-0.5 rounded-full"
              style={{
                color: "white",
                background: "rgba(0,0,0,0.35)",
                border: `1px solid ${accent}80`,
              }}
            >
              <Lock className="w-2.5 h-2.5" /> Paid
            </span>
          ) : (
            <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#00E5FF]">Free</span>
          )}
        </div>
        {/* Tier badge — always visible based on pack tier */}
        <span
          className="inline-flex items-center text-[9px] font-black tracking-[0.14em] px-2 py-[2px] rounded-full uppercase mb-1.5"
          style={
            pack.tier === "premium"
              ? {
                  background: "linear-gradient(135deg, #FF2E9A, #FF6B47)",
                  color: "#FFFFFF",
                  border: "1px solid rgba(255, 255, 255, 0.32)",
                  boxShadow: "0 0 10px rgba(255, 46, 154, 0.6)",
                }
              : {
                  background: "rgba(61, 214, 140, 0.16)",
                  color: "#7DE6A9",
                  border: "1px solid rgba(61, 214, 140, 0.6)",
                  boxShadow: "0 0 6px rgba(61, 214, 140, 0.4)",
                }
          }
          data-testid={`pack-showcase-tier-${pack.id}`}
        >
          {pack.tier === "premium" ? "💎 Premium" : "✓ Free"}
        </span>
        {pack.tag && (
          <span
            className="inline-block mb-1.5 text-[9px] font-bold uppercase tracking-[0.25em] text-white px-2 py-0.5 rounded-full"
            style={{ background: accent }}
          >
            {pack.tag}
          </span>
        )}
        <p className="font-bold text-white text-base leading-tight mb-1 tracking-tight">
          {pack.name}
        </p>
        <p
          className="text-[11px] leading-snug line-clamp-2"
          style={isPremium ? { color: "rgba(255,255,255,0.85)" } : { color: "#C9C7D9" }}
        >
          {pack.description}
        </p>
        <div className="mt-3 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.2em] text-white/80">
          {isPremium ? "Tap to peek 👀" : "Tap to select →"}
        </div>
      </div>
    </motion.button>
  );
}

function PackChip({ pack, active, unlocked, onSelect, onLocked }) {
  const [shaking, setShaking] = useState(false);
  const premium = !unlocked;
  const tagline = pack.description;
  return (
    <motion.button
      type="button"
      animate={shaking ? { x: [0, -6, 6, -4, 4, 0] } : { x: 0 }}
      transition={{ duration: 0.35 }}
      onClick={() => {
        if (premium) {
          setShaking(true);
          try { navigator.vibrate?.(18); } catch (_e) { /* noop */ }
          setTimeout(() => {
            setShaking(false);
            onLocked();
          }, 280);
          return;
        }
        onSelect();
      }}
      data-testid={`pack-chip-${pack.id}`}
      className={`relative px-3 py-1.5 rounded-full text-xs font-bold border transition overflow-hidden ${
        active ? "text-white" : unlocked ? "bg-white/[0.03] border-white/10 text-[#C9C7D9] hover:text-white" : "text-white"
      }`}
      style={
        active
          ? {
              background: `${pack.color}22`,
              borderColor: `${pack.color}88`,
              boxShadow: `0 0 16px ${pack.color}44`,
            }
          : premium
          ? {
              background: "linear-gradient(135deg, rgba(255,77,141,0.18), rgba(123,97,255,0.18))",
              borderColor: "rgba(255,77,141,0.55)",
              boxShadow: "0 0 18px rgba(255,77,141,0.35)",
            }
          : undefined
      }
    >
      {premium && (
        <>
          <motion.span
            aria-hidden
            className="absolute inset-0 pointer-events-none rounded-full"
            animate={{ opacity: [0.35, 0.7, 0.35] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
            style={{ boxShadow: "inset 0 0 20px rgba(255,77,141,0.45)" }}
          />
          <motion.span
            aria-hidden
            className="absolute inset-0 pointer-events-none rounded-full"
            animate={{ backgroundPositionX: ["0%", "200%"] }}
            transition={{ duration: 2.6, repeat: Infinity, ease: "linear" }}
            style={{
              background: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.30) 50%, transparent 100%)",
              backgroundSize: "50% 100%",
              backgroundRepeat: "no-repeat",
            }}
          />
        </>
      )}
      <span className="relative inline-flex items-center gap-1">
        {premium && <Lock className="w-3 h-3 text-[#FF4D8D]" />}
        <span>{pack.emoji}</span>
        <span>{pack.name}</span>
        {premium && tagline && (
          <span className="hidden sm:inline text-[10px] font-semibold text-white/80 normal-case tracking-normal ml-1">
            — "{tagline}"
          </span>
        )}
      </span>
    </motion.button>
  );
}


function MiniModeCard({ gradient, glow, icon, title, desc, hoverGlow, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group text-left card-glow rounded-3xl p-6 cursor-pointer transition-all duration-300 border border-white/5 hover:-translate-y-1 hover:scale-[1.03] hover:border-white/30 active:scale-[1.01]"
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = `0 0 0 1px ${hoverGlow}, 0 24px 60px rgba(0,0,0,0.5), 0 0 50px ${hoverGlow}`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "";
      }}
      data-testid="mini-mode-card"
    >
      <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${gradient} flex items-center justify-center mb-4 ${glow} transition-all duration-300 group-hover:scale-110 group-hover:rotate-3`}>
        {icon}
      </div>
      <h3 className="text-base font-bold text-white mb-1 tracking-tight">{title}</h3>
      <p className="text-sm text-[#C9C7D9]">{desc}</p>
    </button>
  );
}

function PremiumStrip({ isPremium, usage, intro, onPaywall, onPricing, onTeaser, onIntro }) {
  if (isPremium) {
    return (
      <div
        className="inline-flex items-center gap-2 mt-4 px-4 py-1.5 rounded-full border border-[#FF4D8D]/40 bg-[#FF4D8D]/10 text-[#FF4D8D] text-xs font-bold uppercase tracking-[0.25em]"
        data-testid="premium-active-badge"
      >
        <Crown className="w-3.5 h-3.5" /> Premium
      </div>
    );
  }
  return (
    <div className="mt-5" data-testid="premium-strip">
      <SpicyDealBar onClick={onIntro} />
      <p className="text-[11px] text-[#C9C7D9] mt-2 ml-1">
        Includes bold questions, wild dares & no filters 😈
      </p>
    </div>
  );
}

function SpicyDealBar({ onClick }) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileTap={{ scale: 0.97 }}
      animate={{ scale: [1, 1.02, 1] }}
      transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
      className="relative overflow-hidden rounded-full pl-3 pr-4 py-2 border-2 inline-flex items-center gap-2.5 text-sm font-bold"
      style={{
        background: "linear-gradient(135deg, rgba(255,77,141,0.22), rgba(123,97,255,0.22))",
        borderColor: "rgba(255,77,141,0.75)",
        boxShadow: "0 0 30px rgba(255,77,141,0.45)",
      }}
      data-testid="spicy-deal-bar"
    >
      <motion.span
        aria-hidden
        className="absolute inset-0 pointer-events-none rounded-full"
        animate={{ backgroundPositionX: ["0%", "200%"] }}
        transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
        style={{
          background: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.22) 50%, transparent 100%)",
          backgroundSize: "45% 100%",
          backgroundRepeat: "no-repeat",
        }}
      />
      <motion.span
        className="text-xl relative"
        animate={{ rotate: [0, 10, -8, 0] }}
        transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
      >
        🌶️
      </motion.span>
      <span className="relative text-[10px] font-bold uppercase tracking-[0.25em] bg-[#FF4D8D] text-white px-2 py-0.5 rounded-full">
        24hr deal
      </span>
      <span className="relative text-white">Spicy Mode</span>
      <span className="relative font-display text-lg text-white">$0.99</span>
      <span className="relative text-[10px] text-[#C9C7D9] line-through">$1.99</span>
    </motion.button>
  );
}

function PremiumOffersStrip({ catalog, user }) {
  const introLink = catalog?.intro?.payment_link;
  const bundleLink = catalog?.bundle?.payment_link;
  const introPrice = catalog?.intro?.amount ?? 0.99;
  const bundlePrice = catalog?.bundle?.amount ?? 4.99;

  const buy = async (link, label) => {
    const ok = await openStripeLink(link, { user });
    if (!ok) toast.error(`Couldn't open ${label} checkout`);
  };

  return (
    <section className="mt-14 md:mt-16" data-testid="premium-offers-strip">
      {/* Big bundle banner */}
      <motion.button
        type="button"
        onClick={() => buy(bundleLink, "Unlock all")}
        whileHover={{ y: -2 }}
        whileTap={{ scale: 0.98 }}
        className="relative w-full text-left rounded-3xl p-5 md:p-6 overflow-hidden border group"
        style={{
          background:
            "linear-gradient(135deg, rgba(255,77,141,0.16), rgba(123,97,255,0.16)), radial-gradient(circle at 80% 20%, rgba(0,229,255,0.12), rgba(20,15,30,0.85) 70%)",
          borderColor: "rgba(255,77,141,0.45)",
          boxShadow:
            "0 14px 42px rgba(0,0,0,0.4), 0 0 36px rgba(255,77,141,0.18)",
        }}
        data-testid="bundle-banner"
      >
        <motion.span
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          animate={{ backgroundPositionX: ["0%", "200%"] }}
          transition={{ duration: 3.4, repeat: Infinity, ease: "linear" }}
          style={{
            background:
              "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.18) 50%, transparent 100%)",
            backgroundSize: "45% 100%",
            backgroundRepeat: "no-repeat",
          }}
        />
        <div className="relative flex items-center gap-4 md:gap-5">
          <motion.span
            className="text-4xl md:text-5xl flex-shrink-0"
            animate={{ rotate: [0, 8, -6, 0], scale: [1, 1.08, 1] }}
            transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut" }}
            style={{ filter: "drop-shadow(0 0 14px rgba(255,77,141,0.7))" }}
          >
            🔓
          </motion.span>
          <div className="flex-1 min-w-0">
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.25em] bg-gradient-to-r from-[#FF4D8D] to-[#7B61FF] text-white px-2 py-0.5 rounded-full mb-1.5">
              Best Value
            </span>
            <h3 className="font-display font-bold text-white text-xl md:text-2xl leading-tight">
              Unlock all packs
            </h3>
            <p className="text-xs md:text-sm text-[#C9C7D9] leading-snug">
              All 8 paid packs · everything · forever 😈
            </p>
          </div>
          <div className="text-right flex-shrink-0">
            <p className="font-display-tight font-bold text-white text-2xl md:text-3xl">
              ${bundlePrice}
            </p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#C9C7D9] font-bold">
              one-time
            </p>
          </div>
        </div>
      </motion.button>

      {/* Spicy 24hr deal */}
      <motion.button
        type="button"
        onClick={() => buy(introLink, "Spicy")}
        whileTap={{ scale: 0.98 }}
        animate={{ scale: [1, 1.015, 1] }}
        transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
        className="relative mt-3 w-full text-left rounded-full pl-4 pr-5 py-3 border inline-flex items-center gap-3 overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, rgba(255,77,141,0.16), rgba(123,97,255,0.14))",
          borderColor: "rgba(255,77,141,0.45)",
          boxShadow: "0 0 22px rgba(255,77,141,0.22)",
        }}
        data-testid="spicy-deal-bar"
      >
        <motion.span
          aria-hidden
          className="absolute inset-0 pointer-events-none rounded-full"
          animate={{ backgroundPositionX: ["0%", "200%"] }}
          transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
          style={{
            background:
              "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.22) 50%, transparent 100%)",
            backgroundSize: "45% 100%",
            backgroundRepeat: "no-repeat",
          }}
        />
        <motion.span
          className="text-2xl relative flex-shrink-0"
          animate={{ rotate: [0, 10, -8, 0] }}
          transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
        >
          🌶️
        </motion.span>
        <span className="relative text-[10px] font-bold uppercase tracking-[0.25em] bg-[#FF4D8D] text-white px-2 py-0.5 rounded-full flex-shrink-0">
          24hr deal
        </span>
        <span className="relative text-white font-bold text-sm md:text-base flex-1 truncate">
          Spicy Mode
        </span>
        <span className="relative font-display-tight text-lg md:text-xl text-white font-bold flex-shrink-0">
          ${introPrice}
        </span>
        <span className="relative text-[11px] text-[#C9C7D9] line-through font-bold flex-shrink-0">
          $1.99
        </span>
      </motion.button>
    </section>
  );
}





function GroupGameplayDispatcher({ code, currentPlayerId, isHost, onLeave }) {
  // Confessions runs on its own state machine; everything else (Most Likely To /
  // Red Flag Radar / Chaos Mode etc.) goes through the standard vote-based
  // gameplay. Poll once on mount to discover the active pack, then route.
  const [packId, setPackId] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.getGroupRoom(code).then((r) => {
      if (!cancelled) setPackId(r?.pack_id || "");
    }).catch(() => { if (!cancelled) setPackId(""); });
    return () => { cancelled = true; };
  }, [code]);

  if (packId === null) {
    return (
      <div className="vc-bg min-h-screen flex items-center justify-center text-white/70">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }
  if (packId === "confessions") {
    return (
      <PlayGroupConfessions
        code={code}
        currentPlayerId={currentPlayerId}
        isHost={isHost}
        onLeave={onLeave}
      />
    );
  }
  return (
    <PlayGroupGameplay
      code={code}
      currentPlayerId={currentPlayerId}
      isHost={isHost}
      onLeave={onLeave}
    />
  );
}
